import { useCallback, useEffect, useRef, useState } from 'react';
import type { StructuredAddress } from '../utils/address-formatting';
import { extractAddressFromGooglePlace } from '../utils/address-formatting';
import { useGoogleMapsLoader } from './useGoogleMapsLoader';

export interface GooglePlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types?: string[];
}

export interface GooglePlaceDetails {
  place_id: string;
  name?: string;
  formatted_address: string;
  address: StructuredAddress;
}

interface UseGooglePlacesSearchReturn {
  predictions: GooglePlacePrediction[];
  loading: boolean;
  error: string | null;
  searchBusinesses: (query: string) => Promise<void>;
  getPlaceDetails: (placeId: string) => Promise<GooglePlaceDetails | null>;
  clearPredictions: () => void;
}

/**
 * Custom hook for Google Places business search and place details.
 * Uses the Google Maps JS SDK directly (no backend proxy needed).
 */
export function useGooglePlacesSearch(debounceMs = 300): UseGooglePlacesSearchReturn {
  const { isLoaded } = useGoogleMapsLoader();
  const [predictions, setPredictions] = useState<GooglePlacePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const placesServiceDivRef = useRef<HTMLDivElement | null>(null);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const getAutocompleteService = useCallback(() => {
    if (!isLoaded) return null;
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
    }
    return autocompleteServiceRef.current;
  }, [isLoaded]);

  const getPlacesService = useCallback(() => {
    if (!isLoaded) return null;
    if (!placesServiceRef.current) {
      // PlacesService requires an HTMLDivElement or Map instance
      if (!placesServiceDivRef.current) {
        placesServiceDivRef.current = document.createElement('div');
      }
      placesServiceRef.current = new google.maps.places.PlacesService(placesServiceDivRef.current);
    }
    return placesServiceRef.current;
  }, [isLoaded]);

  const searchBusinesses = useCallback(
    async (query: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!query || query.length < 2) {
        setPredictions([]);
        setError(null);
        return;
      }

      debounceTimerRef.current = setTimeout(async () => {
        const service = getAutocompleteService();
        if (!service) {
          setError('Google Maps not loaded');
          return;
        }

        setLoading(true);
        setError(null);

        try {
          const response = await service.getPlacePredictions({
            input: query,
            types: ['establishment'],
          });

          const mapped: GooglePlacePrediction[] = (response.predictions || []).map((p) => ({
            place_id: p.place_id,
            description: p.description,
            structured_formatting: {
              main_text: p.structured_formatting.main_text,
              secondary_text: p.structured_formatting.secondary_text,
            },
            types: p.types,
          }));

          setPredictions(mapped);
        } catch (err) {
          console.error('Error searching businesses:', err);
          setError(err instanceof Error ? err.message : 'Failed to search businesses');
          setPredictions([]);
        } finally {
          setLoading(false);
        }
      }, debounceMs);
    },
    [debounceMs, getAutocompleteService]
  );

  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<GooglePlaceDetails | null> => {
      if (!placeId) return null;

      const service = getPlacesService();
      if (!service) {
        setError('Google Maps not loaded');
        return null;
      }

      setLoading(true);
      setError(null);

      return new Promise((resolve) => {
        service.getDetails(
          {
            placeId,
            fields: ['place_id', 'name', 'formatted_address', 'address_components'],
          },
          (place, status) => {
            setLoading(false);

            if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
              console.error('Error fetching place details:', status);
              setError(`Failed to fetch place details: ${status}`);
              resolve(null);
              return;
            }

            const structuredAddress = extractAddressFromGooglePlace({
              place_id: place.place_id || placeId,
              name: place.name,
              formatted_address: place.formatted_address || '',
              address_components: (place.address_components || []).map((c) => ({
                long_name: c.long_name,
                short_name: c.short_name,
                types: c.types,
              })),
            });

            resolve({
              place_id: place.place_id || placeId,
              name: place.name,
              formatted_address: place.formatted_address || '',
              address: structuredAddress,
            });
          }
        );
      });
    },
    [getPlacesService]
  );

  const clearPredictions = useCallback(() => {
    setPredictions([]);
    setError(null);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, []);

  return {
    predictions,
    loading,
    error,
    searchBusinesses,
    getPlaceDetails,
    clearPredictions,
  };
}
